import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search } from 'lucide-react';
import SearchOverlay from './SearchOverlay';
import { courses as localCourses } from '../data/courses';
import { API_URL } from '../apiConfig';





const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [courses, setCourses] = useState(localCourses);
  const [searchText, setSearchText] = useState('');

  const fullText = 'Search';

  const toggleMenu = () => setIsOpen(prev => !prev);
  const closeMenu = () => setIsOpen(false);

  // Function to check active route
  const isActive = (path) => location.pathname === path;

  // Fetch courses from API for search functionality
  useEffect(() => {
    console.log('Navbar: Fetching courses from:', API_URL);
    fetch(API_URL)

      .then(res => res.json())
      .then(data => {
        console.log('Navbar: Search API Data:', data);
        const apiCourses = Array.isArray(data) ? data : (data.courses || []);
        if (apiCourses.length > 0) {
          const mappedApiCourses = apiCourses.map((c, index) => ({
            id: `api-${index}`,
            title: c.course_title,
            bannerImage: "/course1.avif", // Placeholder
            isApi: true
          }));
          setCourses([...localCourses, ...mappedApiCourses]);
        }
      })
      .catch(err => console.error('Navbar Fetch error:', err));
  }, []);



  // Ctrl+K search shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Typing animation
  useEffect(() => {
    let index = 0;
    const interval = setInterval(() => {
      if (index <= fullText.length) {
        setSearchText(fullText.slice(0, index));
        index++;
      } else {
        clearInterval(interval);
        setTimeout(() => {
          index = 0;
          setSearchText('');
        }, 2000);
      }
    }, 150);

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <nav className="flex items-center justify-between px-6 lg:px-12 py-5 bg-white/95 backdrop-blur-md fixed top-0 left-0 right-0 z-50 shadow-lg border-b border-gray-100 transition-all duration-300">

        {/* LOGO */}
        <Link
          to="/"
          className="flex items-center transform hover:scale-105 transition-transform duration-300"
          onClick={closeMenu}
        >
          <img
            src="/logo.png"
            alt="Greenmark Logo"
            className="h-24 sm:h-28 md:h-32 lg:h-20 w-auto object-contain mix-blend-multiply"
          />
        </Link>

        {/* DESKTOP MENU */}
        <ul className="hidden lg:flex space-x-4 text-gray-800 font-medium items-center">

          {[
            { path: "/", label: "Home" },
            { path: "/about", label: "About" },
            { path: "/courses", label: "Courses" },
            { path: "/blog", label: "Blog" },
            { path: "/contact", label: "Contact" },
            { path: "/enhanced-r1", label: "Enhanced R1 Framework" },
            
          ].map(({ path, label }) => (
            <Link
              key={path}
              to={path}
              onClick={closeMenu}
              className={`
                px-4 py-2 rounded-full transition-all duration-300
                ${isActive(path)
                  ? "bg-[#0b9949] text-white shadow-md scale-105"
                  : "hover:bg-gray-100 hover:text-[#0b9949]"
                }
              `}
            >
              {label}
            </Link>
          ))}

          {/* Search Button */}
          <button
            onClick={() => setIsSearchOpen(true)}
            className="ml-4 px-4 py-2 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center gap-2 transition-all duration-300"
          >
            <Search className="w-4 h-4 text-[#0b9949]" />
            <span>{searchText || "Search"}</span>
          </button>
        </ul>

        {/* MOBILE MENU BUTTON */}
        <button
          onClick={toggleMenu}
          className="lg:hidden text-[#0b9949] focus:outline-none transform hover:scale-110 transition-transform duration-300"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor">
            {isOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8h16M4 16h16" />
            )}
          </svg>
        </button>

        {/* MOBILE MENU */}
        {isOpen && (
          <div className="absolute top-full left-0 w-full bg-white backdrop-blur-md shadow-xl lg:hidden border-b border-gray-100">
            <ul className="flex flex-col space-y-4 py-6 px-6 text-gray-800 font-medium">

              {[
                { path: "/", label: "Home" },
                { path: "/about", label: "About" },
                { path: "/courses", label: "Courses" },
                { path: "/blog", label: "Blog" },
                { path: "/contact", label: "Contact" },
                { path: "/enhanced-r1", label: "Enhanced R1 Framework" },
              ].map(({ path, label }) => (
                <Link
                  key={path}
                  to={path}
                  onClick={closeMenu}
                  className={`
                    px-4 py-2 rounded-full transition-all duration-300
                    ${isActive(path)
                      ? "bg-[#0b9949] text-white shadow-md"
                      : "hover:bg-gray-100 hover:text-[#0b9949]"
                    }
                  `}
                >
                  {label}
                </Link>
              ))}

              {/* Mobile Search */}
              <button
                onClick={() => { setIsSearchOpen(true); closeMenu(); }}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-all duration-300"
              >
                <Search className="w-4 h-4 text-[#0b9949]" /> Search Courses
              </button>

              {/* Mobile Join Button */}
              <button
                onClick={() => { navigate('/contact'); closeMenu(); }}
                className="text-white px-5 py-3 rounded-full font-bold shadow-lg hover:shadow-xl hover:scale-105 transform transition-all duration-300 group"
                style={{
                  background: 'linear-gradient(to bottom, #0a6e35, #0b9949)',
                }}
              >
                <span className="flex items-center justify-center gap-2">
                  Join Now <span className="text-xl transition-transform duration-300 group-hover:translate-x-1">→</span>
                </span>
              </button>
            </ul>
          </div>
        )}

        {/* DESKTOP JOIN NOW */}
        <div className="hidden lg:block">
          <button
            onClick={() => navigate('/contact')}
            className="text-white px-8 py-3 rounded-full font-bold text-base shadow-lg hover:shadow-2xl hover:scale-[1.05] transform transition-all duration-300 whitespace-nowrap group"
            style={{
              background: 'linear-gradient(to bottom, #0a6e35, #0b9949)',
            }}
          >
            <span className="flex items-center gap-2">
              Join Now <span className="text-lg transition-transform duration-300 group-hover:translate-x-1">→</span>
            </span>
          </button>
        </div>
      </nav>

      {/* Search Overlay */}
      <SearchOverlay
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        courses={courses}
      />
    </>
  );
};

export default Navbar;
